import React, { useEffect, useState } from 'react';
import ModalContainer from '../../components/ModalContainer.jsx';
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import GetField from '../../components/GetField.jsx';
import Alert from '../../utils/Alert.js';
import { getSingleMaterialService } from '../../services/materialsService.js';
import { initialTouched, initialValues, onSubmit, validationSchema } from './addMaterialFormikCodes.js';
import { Form, Formik } from 'formik';

const fieldNames = [
    {
        name: "afnor",
        type: "text",
        placeholder: "AFNOR"
    },
    {
        name: "aisi_sae",
        type: "text",
        placeholder: "AISI / SAE"
    },
    {
        name: "astm",
        type: "text",
        placeholder: "ASTM"
    },
    {
        name: "bts",
        type: "text",
        placeholder: "BTS"
    },
    {
        name: "sis",
        type: "text",
        placeholder: "SIS"
    },
    {
        name: "uni",
        type: "text",
        placeholder: "UNI"
    },
    {
        name: "uns",
        type: "text",
        placeholder: "UNS"
    },
    {
        name: "wnr",
        type: "text",
        placeholder: "WNR"
    },
    {
        name: "din",
        type: "text",
        placeholder: "DIN"
    },
    {
        name: "en",
        type: "text",
        placeholder: "EN"
    },
    {
        name: "jis",
        type: "text",
        placeholder: "JIS"
    },
    {
        name: "english_group",
        type: "text",
        placeholder: "گروه انگلیسی"
    },
    {
        name: "persian_group",
        type: "text",
        placeholder: "گروه فارسی"
    },
    {
        name: "class_name",
        type: "text",
        placeholder: "کلاس انگلیسی"
    },
    {
        name: "persian_class",
        type: "text",
        placeholder: "کلاس فارسی"
    },
    {
        name: "cost",
        type: "text",
        placeholder: "هزینه (یک کیلو گرم)",
        label: "هزینه"
    },
    {
        name: "density",
        type: "text",
        placeholder: "چگالی (kg/m3)",
        label: "چگالی"
    },
    {
        name: "machinability",
        type: "text",
        placeholder: "ماشین‌کاری (درصد)",
        label: "ماشین‌کاری"
    },
    {
        name: "manufacturer_reference",
        type: "text",
        placeholder: "مرجع سازنده",
        label: "مرجع"
    },
    {
        name: "link",
        type: "text",
        placeholder: "آدرس ماده اولیه",
        label: "آدرس"
    },
];

const groupNameOptions = [
    {
        text: "P",
        value: "P"
    },
    {
        text: "M",
        value: "M"
    },
    {
        text: "K",
        value: "K"
    },
    {
        text: "N",
        value: "N"
    },
    {
        text: "S",
        value: "S"
    },
    {
        text: "H",
        value: "H"
    }
];


const AddMaterial = () => {
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [reinitializeValues, setReinitializeValues] = useState(null);

    const { setData, dataLength, itemsCnt } = useOutletContext();

    const navigate = useNavigate();
    const location = useLocation();

    const handleGetEditingMaterial = async (materialId) => {
        try {
            const response = await getSingleMaterialService(materialId);
            if (response.status === 200) {
                setEditingMaterial(response.data);
            }
        } catch (error) {
            Alert(`خطا ${error.response?.status || ""}!`, "در دریافت اطلاعات ماده اولیه مشکلی رخ داده است!\nلطفاً مجدداً تلاش کنید.", "error");
            return navigate(-1);
        }
    }

    useEffect(() => {
        if (location.state) {
            handleGetEditingMaterial(location.state.id);
        } else {
            setEditingMaterial(null);
        }
    }, []);

    useEffect(() => {
        if (editingMaterial) {
            setReinitializeValues({
                group_name: editingMaterial.group_name || "",
                afnor: editingMaterial.afnor || "",
                aisi_sae: editingMaterial.aisi_sae || "",
                astm: editingMaterial.astm || "",
                bts: editingMaterial.bts || "",
                sis: editingMaterial.sis || "",
                uni: editingMaterial.uni || "",
                uns: editingMaterial.uns || "",
                wnr: editingMaterial.wnr || "",
                din: editingMaterial.din || "",
                en: editingMaterial.en || "",
                jis: editingMaterial.jis || "",
                english_group: editingMaterial.english_group || "",
                persian_group: editingMaterial.persian_group || "",
                class_name: editingMaterial.class_name || "",
                persian_class: editingMaterial.persian_class || "",
                cost: editingMaterial.cost || "0",
                density: editingMaterial.density || "0",
                machinability: editingMaterial.machinability || "",
                manufacturer_reference: editingMaterial.manufacturer_reference || "",
                link: editingMaterial.link || ""
            })
        } else {
            setReinitializeValues(null);
        }
    }, [editingMaterial]);

    return (
        <ModalContainer
            alwaysOpen={true}
            fullscreen={false}
            title={location.state ?
                "ویرایش ماده اولیه" :
                "افزودن ماده اولیه"
            }
        >
            <Formik
                initialValues={reinitializeValues || initialValues}
                initialTouched={reinitializeValues ? initialTouched : {}}
                onSubmit={(values, actions) => onSubmit(values, actions, editingMaterial, setData, dataLength, itemsCnt, navigate)}
                validationSchema={validationSchema}
                enableReinitialize
                validateOnMount
            >
                {(formik) => {
                    return (
                        <Form className="flex flex-col items-center">
                            <div
                                className="mb-5 w-full md:w-4/6 max-w-screen-sm"
                            >
                                <GetField
                                    control="select"
                                    name="group_name"
                                    options={groupNameOptions}
                                    label="گروه:"
                                    title="گروه را انتخاب کنید"
                                />
                            </div>
                            {fieldNames.map((item, index) => (
                                <div
                                    key={`materialField_${index}`}
                                    className="mb-5 w-full md:w-4/6 max-w-screen-sm"
                                >
                                    <GetField
                                        control="input"
                                        name={item.name}
                                        type={item.type}
                                        placeholder={item.placeholder}
                                        label={item.label || item.placeholder}
                                        formik={formik}
                                    />
                                </div>
                            ))}
                            <div className="w-full text-center mt-4">
                                <GetField
                                    control="submitbutton"
                                    formik={formik}
                                    text={location.state ?
                                        "ویرایش ماده اولیه" :
                                        "افزودن ماده اولیه"
                                    }
                                />
                            </div>
                        </Form>
                    );
                }}
            </Formik>
        </ModalContainer>
    );
}

export default AddMaterial;
